import React from 'react';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import Modal from '../../components/modals/Modal';
import { CarType } from '../../types/bundleTypes';
import { Region, regionNames, RegionsList } from '../../types/orderTypes';
import { toggleElement } from '../../utils/array';
import { capitalize } from '../../utils/strings';
import { CourierTravelMode, Role, User } from '../../types/userTypes';
import { UpdateFeedback } from './EditSenderUserModal';
import './EditUserModal.scss';
import { postUpdateUser } from './postUpdateUser';
import { IsThereAnyErrors, validateFields } from './validateFields';
import { sendPasswordResetLink } from './sendPasswordResetLink';
import { EditFormGroupModal } from './EditFormGroupModal';

const EDIT_USER_MODIFIED_CLASS = 'edit-user-modal__modified';

export interface EditUser {
  role: Role;
  email: string;
  emailVerified: boolean;
  newEmail: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  companyName?: string;
  companyNumber?: string;
  country?: string;
  paymentByInvoiceAllowed: boolean;
  isCourier: boolean;
  isSorter: boolean;
  travelMode: CourierTravelMode;
  carType?: CarType;
  plateNumbers?: string[];
  isLocked: boolean;
  accountId?: number | null;
  etaOffsetSecondsInput: string;
  etaOffsetSeconds: number;
  regions: Region[];
  limitedAccess: boolean;
  createAccount: boolean;
  preferredGeoZones?: string[];
  notes?: string;
  predictionModel?: string;
}

export interface UpdateResult {
  success: boolean;
  errors: ErrorMsg[];
}

export interface ErrorMsg {
  message: string;
}

interface State {
  user: EditUser;
  courierId: string;
  courierEtaDiff?: number;
  userUpdated: boolean;
  updatingUser: boolean;
  fieldErrors: {
    [key: string]: ErrorMsg | undefined;
  };
  errors: ErrorMsg[];
  resettingPassword: boolean;
}

interface Props {
  onCloseModal: (updated: boolean) => void;
  user: User;
}

export function RoleRow({ role }: { role: string }): JSX.Element {
  return (
    <div className="edit-user-modal__form-group">
      <label>
        <span className="edit-user-modal__form-label">Role</span>
        <input
          type="text"
          data-lpignore="true"
          name="role"
          value={capitalize(role.toLowerCase()).replace('_', ' ')}
          readOnly={true}
        />
      </label>
    </div>
  );
}

export function EmailRow({ email }: { email: string }): JSX.Element {
  return (
    <div className="edit-user-modal__form-group">
      <label>
        <span className="edit-user-modal__form-label">Email</span>
        <input
          type="email"
          data-lpignore="true"
          placeholder="example@porterbuddy.com"
          name="email"
          value={email}
          readOnly={true}
        />
      </label>
    </div>
  );
}

export function NotesRow({
  notes,
  onChange,
}: {
  notes: string;
  onChange: any;
}): JSX.Element {
  return (
    <div className="edit-user-modal__form-group">
      <label>
        <span className="edit-user-modal__form-label">Notes</span>
        <textarea
          data-lpignore="true"
          className={'textarea-modal_form-group'}
          placeholder=""
          name="notes"
          rows={4}
          maxLength={280}
          value={notes}
          onChange={onChange}
        />
      </label>
    </div>
  );
}

export function ShowCourierIdRow({
  courierId,
}: {
  courierId: string;
}): JSX.Element {
  return (
    <div className={'edit-user-modal__form-group'}>
      <label>
        <span className="edit-user-modal__form-label">Courier Id</span>
        <input type="text" name="courierId" value={courierId} readOnly={true} />
      </label>
    </div>
  );
}

export function EmailVerified({
  emailVerified,
  onChange,
}: {
  emailVerified: boolean;
  onChange: any;
}): JSX.Element {
  return (
    <div className="edit-user-modal__form-group">
      <label>
        <span className="edit-user-modal__form-label">Email verified</span>
        <div className="edit-user-modal__form-checkbox">
          <input
            type="checkbox"
            name="emailVerified"
            checked={emailVerified}
            onChange={onChange}
          />
          <span>
            {emailVerified && 'The email has been verified'}
            {!emailVerified && 'The email must be verified before signing in'}
          </span>
        </div>
      </label>
    </div>
  );
}

export class EditAdminUserModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      userUpdated: false,
      updatingUser: false,
      courierId: '…',
      user: {
        ...props.user,
        newEmail: '',
        companyName: props.user.companyName || '',
        companyNumber: props.user.companyNumber || '',
        paymentByInvoiceAllowed: props.user.paymentByInvoiceAllowed,
        travelMode: props.user.travelMode || CourierTravelMode.None,
        etaOffsetSeconds: props.user.etaOffsetSeconds || 0,
        etaOffsetSecondsInput: (props.user.etaOffsetSeconds || 0).toString(),
        regions: props.user.regions || [],
        createAccount: false,
      },

      fieldErrors: {},
      errors: [],
      resettingPassword: false,
    };

    this.escFunction = this.escFunction.bind(this);
  }

  updateUser = (updatedUserFields: Partial<EditUser>): void => {
    const { user } = this.state;
    const updatedUser = { ...user, ...updatedUserFields };
    this.setState({ user: updatedUser, userUpdated: false });

    if (IsThereAnyErrors(this.state.fieldErrors)) {
      const fieldErrors = validateFields(updatedUser);
      this.setState({ fieldErrors: fieldErrors });
    }
  };

  escFunction(event: { keyCode: number }): void {
    if (event.keyCode === 27) {
      this.props.onCloseModal(this.state.userUpdated);
    }
  }

  async componentDidMount(): Promise<void> {
    document.addEventListener('keydown', this.escFunction, false);
  }

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.escFunction, false);
  }

  async saveUser(e: { preventDefault: () => void }): Promise<void | undefined> {
    e.preventDefault();
    const { user } = this.state;
    const validatedFieldErrors = validateFields(user);
    this.setState({ fieldErrors: validatedFieldErrors });

    if (IsThereAnyErrors(validatedFieldErrors)) return;

    this.setState({
      userUpdated: false,
      errors: [],
      updatingUser: true,
    });
    const result: UpdateResult = await postUpdateUser(
      user,
      this.props.user.emailVerified
    );

    this.setState({
      userUpdated: !!result.success,
      errors: result.success ? [] : result.errors,
      updatingUser: false,
    });
  }

  async sendPasswordReset(e: React.MouseEvent): Promise<void> {
    e.preventDefault();
    this.setState({ resettingPassword: true });
    const { user } = this.state;
    const linkSent = await sendPasswordResetLink(user.email);
    if (linkSent) {
      this.props.onCloseModal(false);
    } else {
      this.setState({ resettingPassword: false });
    }
  }

  render(): JSX.Element {
    const width = 800;

    const {
      user,
      errors,
      fieldErrors,
      userUpdated,
      updatingUser,
      resettingPassword,
    } = this.state;

    const saveButton = {
      title: 'Save',
      disabled: updatingUser,
      onClick: (e: any) => {
        this.saveUser(e);
      },
    };

    const resetPasswordButton = {
      title: 'Reset password',
      disabled: resettingPassword,
      onClick: (e: React.MouseEvent) => {
        this.sendPasswordReset(e);
      },
    };

    const buttons = userUpdated
      ? [resetPasswordButton]
      : [resetPasswordButton, saveButton];

    return (
      <Modal
        title="Edit user"
        onClose={() => this.props.onCloseModal(userUpdated)}
        isLoading={false}
        width={width}
        buttons={buttons}
      >
        <div className="edit-user-modal">
          <form className="edit-user-modal__form" autoComplete="off">
            <RoleRow role={user.role} />
            <EmailRow email={user.email} />
            <EmailVerified
              emailVerified={user.emailVerified}
              onChange={(event: { target: { checked: any } }) =>
                this.updateUser({ emailVerified: event.target.checked })
              }
            />

            <EditFormGroupModal hasError={!!fieldErrors.newEmail}>
              <label>
                <span className="edit-user-modal__form-label">New Email</span>
                <input
                  type="email"
                  data-lpignore="true"
                  placeholder="new-email@domain"
                  name="emailNew"
                  value={user.newEmail}
                  onChange={event =>
                    this.updateUser({ newEmail: event.target.value })
                  }
                  className={
                    user.newEmail && user.newEmail !== this.props.user.email
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.newEmail && <em>{fieldErrors.newEmail.message}</em>}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.regions}>
              <label>
                <span className="edit-user-modal__form-label">Regions</span>
                <div className="edit-user-modal__form-checkboxes">
                  {RegionsList.map(regionItem => (
                    <label key={regionItem}>
                      <input
                        type="checkbox"
                        placeholder=""
                        name={`regions${regionItem}`}
                        checked={user.regions.includes(regionItem)}
                        onChange={event =>
                          this.updateUser({
                            regions: toggleElement(user.regions, regionItem),
                          })
                        }
                      />
                      <span>{capitalize(regionNames[regionItem])}</span>
                    </label>
                  ))}
                </div>
              </label>
              {fieldErrors.regions && <em>{fieldErrors.regions.message}</em>}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.firstName}>
              <label>
                <span className="edit-user-modal__form-label">First name</span>
                <input
                  type="text"
                  data-lpignore="true"
                  placeholder="Name"
                  name="firstName"
                  value={user.firstName}
                  onChange={event =>
                    this.updateUser({ firstName: event.target.value })
                  }
                  className={
                    user.firstName !== this.props.user.firstName
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.firstName && (
                <em>{fieldErrors.firstName.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.lastName}>
              <label>
                <span className="edit-user-modal__form-label">Last name</span>
                <input
                  type="text"
                  data-lpignore="true"
                  placeholder="Last name"
                  name="lastName"
                  value={user.lastName}
                  onChange={event =>
                    this.updateUser({ lastName: event.target.value })
                  }
                  className={
                    user.lastName !== this.props.user.lastName
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.lastName && <em>{fieldErrors.lastName.message}</em>}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.phoneCode}>
              <label>
                <span className="edit-user-modal__form-label">
                  Phone country code
                </span>
                <input
                  type="tel"
                  data-lpignore="true"
                  placeholder="+47"
                  name="phoneCode"
                  value={user.phoneCode}
                  onChange={event =>
                    this.updateUser({ phoneCode: event.target.value })
                  }
                  className={
                    user.phoneCode !== this.props.user.phoneCode
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.phoneCode && (
                <em>{fieldErrors.phoneCode.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.phoneNumber}>
              <label>
                <span className="edit-user-modal__form-label">
                  Phone number
                </span>
                <input
                  type="tel"
                  data-lpignore="true"
                  placeholder=""
                  name="phoneNumber"
                  value={user.phoneNumber}
                  onChange={event =>
                    this.updateUser({ phoneNumber: event.target.value })
                  }
                  className={
                    user.phoneNumber !== this.props.user.phoneNumber
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.phoneNumber && (
                <em>{fieldErrors.phoneNumber.message}</em>
              )}
            </EditFormGroupModal>
            <div className={'edit-user-modal__form-group'}>
              <label>
                <span className="edit-user-modal__form-label" />
                <div className="edit-user-modal__form-checkbox">
                  <input
                    type="checkbox"
                    name="limitedAccess"
                    checked={user.limitedAccess}
                    onChange={event =>
                      this.updateUser({
                        limitedAccess: event.target.checked,
                      })
                    }
                  />
                  &nbsp;Limited Access
                </div>
              </label>
            </div>
            <div className="edit-user-modal__form-group">
              <label>
                <span className="edit-user-modal__form-label" />
                <div className="edit-user-modal__form-checkbox">
                  <input
                    type="checkbox"
                    name="isLocked"
                    checked={user.isLocked}
                    onChange={event =>
                      this.updateUser({
                        isLocked: event.target.checked,
                      })
                    }
                  />
                  {user.isLocked && (
                    <>
                      <span className="edit-user-modal__form-lock-icon">
                        <FaLock />
                      </span>
                      &nbsp;
                      <span>Locked</span>
                    </>
                  )}
                  {!user.isLocked && (
                    <>
                      <span className="edit-user-modal__form-lock-icon">
                        <FaLockOpen />
                      </span>
                      &nbsp;
                      <span>Unlocked</span>
                    </>
                  )}
                </div>
              </label>
            </div>

            <UpdateFeedback
              updatingUser={updatingUser}
              errors={errors}
              userUpdated={userUpdated}
            />
          </form>
        </div>
      </Modal>
    );
  }
}
